import * as React from 'react'
import DialogTitle from '@mui/material/DialogTitle'
import Button from '@mui/material/Button'

import Grid from '@mui/material/Grid'
import SubImportMenuItem from './SubImportMenuItem'
import Container from '@mui/material/Container'

import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Dialog from '@mui/material/Dialog'
import LoadingButton from '@mui/lab/LoadingButton'
import Link from '@mui/material/Link'
import FormHelperText from '@mui/material/FormHelperText'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'

import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import StepButton from '@mui/material/StepButton'

import Typography from '@mui/material/Typography'

import ArrowForwardIcon from '@mui/icons-material/ArrowForwardOutlined'
import MultipleSelect from '../MultiSelect'
import EditableSelect from '../EditableSelect'

import TextField from '@mui/material/TextField'

import { useZustand } from '../../hooks/useZustand'
import {
    useContextsStore,
    selectOwnedContexts,
} from '../../stores/useContextsStore'
import usePageInfo from '../../hooks/usePageInfo'

import { UserContext } from '../../UserContext'

import { useTheme } from '@mui/material/styles'

import { SubImportContext } from './SubImportContext'
import { DialogsContext, DialogsProvider } from '../../DialogsContext'

import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'

import axios from 'axios'
import qs from 'qs'

import { useDropzone } from 'react-dropzone'
import {
    Box,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    ListItemSecondaryAction,
} from '@mui/material'
import { InsertDriveFile, Delete } from '@mui/icons-material'

import { slugifyString } from '../../utils/index'
import AdvancedImportSettings from './AdvancedImportSettings'
import ImportingNotice from './ImportingNotice'
import { importErrorMessage } from './importError'
import { useSimplifiedImportAtMount } from '../../hooks/useSimplifiedImportAtMount'

const addPrivateStatementsSelector = state => state.addPrivateStatementsToStore

const steps = ['Start', 'Sources', 'Settings', 'Results']

// The page that holds the file drop, and the page whose effect submits.
// Onboarding folds the 'Settings' page between them into the file page.
const SOURCES_STEP = 1
const SUBMIT_STEP = 3

const topHeader = {
    default:
        'Analyze the content of PDF, Text, Docx, Markdown, CSV, or JSON Files',
    mdFilesImport: 'Analyze the Content of Markdown (MD) Files',
    srtFilesImport: 'Analyze the Content Audio Transcript (SRT) Files',
}

// Bracket-processing modes: a short name per option, with the full behavior
// as a caption underneath. The closed select shows just the name (renderValue).
const WIKILINKS_OPTIONS = [
    {
        value: 'PROCESS_AS_HASHTAGS',
        name: 'Link to nearby concepts',
        caption:
            '[[wiki-links]] connect to other [[wiki-links]] and the concepts used near them - recommended for files',
    },
    {
        value: 'PROCESS_AS_MENTIONS',
        name: 'Link to the whole note',
        caption:
            '[[wiki-links]] connect to all [[wiki-links]] and concepts in the note — enhanced analysis for Obsidian-style vaults',
    },
    {
        value: 'PROCESS_AS_HASHTAGS_IGNORE_THE_REST',
        name: 'Wiki-links only',
        caption:
            'build the graph from [[wiki-links]] alone, ignoring the rest of the text - exactly as in Obsidian',
    },
    {
        value: 'EXCLUDE',
        name: 'Ignore wiki-links',
        caption:
            'leave [[wiki-links]] out and connect only the concepts - best for pure text analysis',
    },
    {
        value: 'IGNORE_BRACKETS',
        name: 'Treat as plain text',
        caption:
            'remove the brackets and process [[wiki-links]] as ordinary words',
    },
]

const SQUARE_BRACKETS_OPTIONS = [
    {
        value: 'IGNORE_BRACKETS',
        name: 'Treat as plain text (recommended)',
        caption: 'remove the brackets and add [text] as ordinary words',
    },
    {
        value: 'USE_AS_CATEGORIES',
        name: 'Use as filter tags',
        caption:
            '[text] becomes a category tag on its block, so you can filter by it later',
    },
    {
        value: 'USE_AS_CATEGORIES_PROCESS_AS_HASHTAGS',
        name: 'Filter tags + graph nodes',
        caption:
            'add [text] as a filter tag and as a node, like a [[wiki-link]]',
    },
    {
        value: 'PROCESS_AS_MENTIONS',
        name: 'Link to the whole block',
        caption:
            '[text] connects to all the words in its block, like an @mention',
    },
]
export default function TextFilesImportDialog({
    presetContext,
    presetMode = 'default',
    open,
    onClose,
}) {
    const theme = useTheme() // hook to get the current theme

    const { label, location } = usePageInfo()

    const {
        activeSubImportDialog,
        setActiveSubImportDialog,
    } = React.useContext(SubImportContext)

    const {
        activeImportDialog,
        setActiveImportDialog,
        openImportDialog,
        closeImportDialog,
        presetFiles,
        setPresetFiles,
    } = React.useContext(DialogsContext)

    const preSelectedContext = presetContext
        ? presetContext
        : generatePresetContext()

    const [selectedContext, addToSelectedContext] = React.useState(
        preSelectedContext
    )

    const [errorMessage, setErrorMessage] = React.useState(null)

    const [importError, setImportError] = React.useState(null)

    const [advancedSettings, setAdvancedSettings] = React.useState(false)

    const [selectedFeeds, setSelectedFeeds] = React.useState([])

    const [fileName, setFileName] = React.useState('')

    const [completedSteps, setCompletedSteps] = React.useState([])

    const [visitedContext, setVisitedContext] = React.useState(false)

    const [filesAsNodes, setFilesAsNodes] = React.useState('0')

    const [filterBySpeakers, setFilterBySpeakers] = React.useState(false)

    const [fileType, setFileType] = React.useState('')

    const [doubleSquareBrackets, setDoubleSquareBrackets] = React.useState('')

    const [squareBrackets, setSquareBrackets] = React.useState(
        'IGNORE_BRACKETS'
    )

    const [doNotSaveGraph, setDoNotSaveGraph] = React.useState(false)

    const [fromInsideContext, setFromInsideContext] = React.useState(false)

    const [modifyAnalyzedText, setModifyAnalyzedText] = React.useState('none')

    const [replaceEntities, setReplaceEntities] = React.useState(false)

    const { username } = React.useContext(UserContext)
    const addPrivateStatementsToStore = useZustand(addPrivateStatementsSelector)
    const contexts = useContextsStore(selectOwnedContexts)

    React.useEffect(() => {
        if (location == 'context' && label) {
            setFromInsideContext(true)
        }
    }, [label])

    const contextNames = contexts.map(c => c.name)

    const handleChange = (_, newValue) => {
        setValue(newValue)
    }

    const [activeStep, setActiveStep] = React.useState(1)
    const [skipped, setSkipped] = React.useState(new Set())

    const isStepOptional = step => {
        return step === 1
    }

    const isStepSkipped = step => {
        return skipped.has(step)
    }

    const handleNext = () => {
        let newSkipped = skipped
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values())
            newSkipped.delete(activeStep)
        }

        if (simplifyImport && activeStep === SOURCES_STEP) {
            prepareForSubmit()
            setActiveStep(SUBMIT_STEP)
        } else {
            setActiveStep(prevActiveStep => prevActiveStep + 1)
        }
        setSkipped(newSkipped)
    }

    const simplifyImport = useSimplifiedImportAtMount()

    const [files, setFiles] = React.useState([])

    const fileInputRef = React.useRef()

    const onDrop = React.useCallback(acceptedFiles => {
        const droppedFiles = acceptedFiles
        handleAddedFiles(droppedFiles)
    }, [])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        noClick: true,
        noKeyboard: true,
        // accept: 'application/pdf, text/plain', // Produces an error but looks like it's not necessary
    })

    const onFilesAdded = event => {
        const addedFiles = Array.from(event.target.files)
        handleAddedFiles(addedFiles)
    }

    const handleAddedFiles = addedFiles => {
        const firstFileType = determineFileType(addedFiles[0])
        const allSameType = addedFiles.every(
            file => determineFileType(file) === firstFileType
        )

        if (addedFiles && addedFiles[0] && addedFiles[0].name)
            setFileName(addedFiles[0].name)

        if (!allSameType) {
            setErrorMessage(
                'If you upload multiple files, please, make sure they are all of the same type and have the same extension.'
            )
            return
        }

        const uploadedFiles = addedFiles.filter(file => {
            const determinedType = determineFileType(file)
            switch (determinedType) {
                case '.pdf':
                    setFileType('pdf')
                    break
                case '.txt':
                    setFileType('txt')
                    break
                case '.srt':
                    setFileType('srt')
                    break
                case '.md':
                    setFileType('md')
                    break
                case '.docx':
                    setFileType('docx')
                    break
                case '.rtf':
                    setFileType('rtf')
                    break
                case '.gexf':
                    if (addedFiles.length > 1) {
                        setErrorMessage(
                            'You can only upload and analyze one Gexf file at a time.'
                        )
                        break
                    }
                    setPresetFiles([file])
                    setActiveImportDialog('gexfFileImport')
                    break
                case '.csv':
                    if (addedFiles.length > 1) {
                        setErrorMessage(
                            'You can only upload and analyze one CSV file at a time.'
                        )
                        break
                    }
                    setPresetFiles([file])
                    setActiveImportDialog('csvFileImport')
                    break
                case '.json':
                case '.jsonl':
                case '.ndjson':
                    if (addedFiles.length > 1) {
                        setErrorMessage(
                            'You can only upload and analyze one JSON or JSON Lines file at a time.'
                        )
                        break
                    }
                    setPresetFiles([file])
                    setActiveImportDialog('jsonFileImport')
                    break
            }
            return (
                determinedType === '.pdf' ||
                determinedType === '.txt' ||
                determinedType === '.srt' ||
                determinedType === '.md' ||
                determinedType === '.docx' ||
                determinedType === '.rtf'
            )
        })

        const uploadedFilesExist =
            uploadedFiles && uploadedFiles.length > 0 ? true : false

        if (!uploadedFilesExist) return

        // No per-plan file-count check here: the bootstrap payload
        // (`/react/user`) carries neither the tier nor the limits, so a client
        // mirror could only guess. lib/uploadLimits.js decides, and the 402 it
        // answers with is surfaced by the submit handler below.
        setFiles(prevFiles => [...prevFiles, ...uploadedFiles])
    }

    const handleDelete = fileToDelete => () => {
        setFiles(prevFiles => prevFiles.filter(file => file !== fileToDelete))
    }

    const openFileDialog = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }

    const fileList = files.reverse().map((file, index) => (
        <ListItem key={index}>
            <ListItemIcon>
                <InsertDriveFile />
            </ListItemIcon>
            <ListItemText
                primary={file.name}
                secondary={`${file.size} bytes`}
            />
            <ListItemSecondaryAction>
                <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={handleDelete(file)}
                >
                    <Delete />
                </IconButton>
            </ListItemSecondaryAction>
        </ListItem>
    ))

    const submitDataForm = () => {
        const formData = new FormData()

        // Append the files to the FormData object
        files.forEach(file => {
            formData.append('uploadedFiles', file) // 'files' is the key for the server
        })

        formData.append('source', 'file')

        formData.append('categoriesAsMentions', filesAsNodes == '1' ? '1' : '0')

        formData.append('isTranscript', filterBySpeakers == '1' ? '1' : '0')

        const fileTypeToSend = fileType == 'md' ? 'markdown' : fileType

        formData.append('extract', fileTypeToSend)

        formData.append('context', selectedContext)

        if (doubleSquareBrackets) {
            formData.append(
                'doubleSquarebracketsProcessing',
                doubleSquareBrackets
            )
        }

        if (squareBrackets) {
            formData.append('squareBracketsProcessing', squareBrackets)
        }

        if (doNotSaveGraph) {
            formData.append('doNotSaveGraph', 'true')
        }

        if (modifyAnalyzedText) {
            formData.append('modifyAnalyzedText', modifyAnalyzedText)
        }

        if (replaceEntities) {
            formData.append('replaceEntities', 'true')
        }

        formData.append('fileNameAsCategory', 'true')

        const postImportPath = fileType == 'md' ? '/import/md' : '/import/files'

        axios
            .post(postImportPath, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data', // Sending as URL-encoded form data
                },
            })
            .then(response => {
                if (response.data.redirectUrl) {
                    // If you're using window.location
                    window.location.href = response.data.redirectUrl

                    // Or, if you're using React Router's history (assuming you have access to the history object)
                    // history.push(response.data.redirectUrl);
                } else {
                    if (response.data.error) {
                        setErrorMessage(
                            typeof response.data.error == 'object'
                                ? response.data.error.error ||
                                      JSON.stringify(response.data.error)
                                : response.data.error
                        )
                        setImportError(response.data.error)
                        setActiveStep(1)
                    }
                    if (response.data.entryuid) {
                        const postedby = username
                        const res = response.data

                        // Handle private mode statements
                        if (doNotSaveGraph && res.statements) {
                            // Private mode: add statements to Zustand store without saving to DB
                            const entryuids = JSON.parse(res.entryuid)
                            const statementHashtags =
                                res.statementHashtags || {}
                            addPrivateStatementsToStore(
                                res.statements,
                                selectedContext,
                                entryuids,
                                statementHashtags
                            )
                        }

                        if (
                            window.myEventEmitter &&
                            myEventEmitter._events['chatMessage']
                        ) {
                            myEventEmitter.emit('chatMessage', {
                                source: null,
                                postedby: postedby,
                                entryuid: JSON.parse(res.entryuid),
                                entrytext: JSON.parse(res.entrytext),
                                entryname: JSON.parse(res.entryname),
                                tags: JSON.parse(res.tags),
                                timestamp: JSON.parse(res.timestamp),
                                graph: res.graph,
                                stimulateVariability: false,
                                message: res.message,
                                statementHashtags: res.statementHashtags,
                                doNotSaveGraph: doNotSaveGraph,
                            })
                            closeImportDialog()
                        }
                    }
                }
            })
            .catch(error => {
                console.error('Received the following error', error)
                const uploadError = importErrorMessage(
                    error,
                    'Could not upload your files. Please, select fewer files and try again.'
                )
                setErrorMessage(uploadError)
                setImportError(uploadError)
                setActiveStep(1)
            })
    }

    const handleBack = () => {
        setActiveStep(prevActiveStep => prevActiveStep - 1)
    }

    const handleSkip = () => {
        if (!isStepOptional(activeStep)) {
            // You probably want to guard against something like this,
            // it should never occur unless someone's actively trying to break something.
            throw new Error("You can't skip a step that isn't optional.")
        }

        setActiveStep(prevActiveStep => prevActiveStep + 1)
        setSkipped(prevSkipped => {
            const newSkipped = new Set(prevSkipped.values())
            newSkipped.add(activeStep)
            return newSkipped
        })
    }

    const handleReset = () => {
        setActiveStep(0)
    }

    React.useEffect(() => {
        setImportError(null)
    }, [selectedFeeds])

    React.useEffect(() => {
        // "Connections Between the Files" is never preselected — analyzing the
        // content is always the default; the user opts into file-name nodes via
        // the Type of Analysis select. Wikilinks follow that choice: content
        // mode links them to nearby concepts only; connections mode switches
        // to mention-style linking (everything in the note) so the
        // note-to-note structure comes through.
        if (fileType === 'md') {
            setDoubleSquareBrackets(
                filesAsNodes === '1'
                    ? 'PROCESS_AS_MENTIONS'
                    : 'PROCESS_AS_HASHTAGS'
            )
            setSquareBrackets('IGNORE_BRACKETS')
        }
        if (fileType == 'srt') {
            setSquareBrackets('USE_AS_CATEGORIES')
            setFilterBySpeakers(true)
        }
    }, [fileType, filesAsNodes])

    React.useEffect(() => {
        if (filterBySpeakers == true && fileType != 'srt') {
            setSquareBrackets('USE_AS_CATEGORIES')
        }
        if (filterBySpeakers == false) {
            setSquareBrackets('IGNORE_BRACKETS')
        }
    }, [filterBySpeakers, fileType])

    React.useEffect(() => {
        switch (activeStep) {
            case 0:
                setActiveImportDialog('import')
                break
            case 2:
                if (files.length > 0) {
                    prepareForSubmit()
                    break
                }
                setErrorMessage('Please, select at least one file.')
                setActiveStep(1)
                break
            case 3:
                if (files.length > 0) {
                    if (
                        completedSteps.indexOf(2) >= 0 &&
                        !selectedContext &&
                        visitedContext
                    ) {
                        setErrorMessage(
                            'Please, select the graph to import your data to.'
                        )
                        break
                    }
                    if (selectedContext) {
                        setCompletedSteps([...completedSteps, 3])
                        setErrorMessage(null)
                        setVisitedContext(true)
                        submitDataForm()
                        break
                    }
                    setActiveStep(2)
                    break
                }
                setActiveStep(2)
                break
        }
    }, [activeStep])

    // The settings page is where the graph name gets defaulted and the earlier
    // steps are marked complete. A guided user never lands on it, so the same
    // preparation has to happen on the way past — without it the submit step
    // bounces back to a page whose fields are collapsed.
    const prepareForSubmit = () => {
        setCompletedSteps([...completedSteps, 0, 1, 2])
        if (!importError) {
            setErrorMessage(null)
        }
        if (!presetContext) {
            addToSelectedContext(generatePresetContext(fileType, fileName))
        }
    }

    const handleStep = step => {
        setActiveStep(step)
    }

    // The settings page as one block. Onboarding folds it into the file page
    // (below the file list); everyone else still gets it on its own page. It
    // has to render INSIDE a page's DialogContent — as a sibling it lands
    // underneath the dialog's action bar.
    const fileProcessingSettings = (
        <AdvancedImportSettings>
            <Grid container spacing={8}>
                <Grid item xs={12} sm={7}>
                    <FormControl fullWidth sx={{ marginTop: 4 }}>
                        <InputLabel id="filesAsNodes-label">
                            Type of Analysis
                        </InputLabel>
                        <Select
                            labelId="filesAsNodes-label"
                            id="filesAsNodes"
                            value={filesAsNodes.toString()}
                            onChange={event =>
                                setFilesAsNodes(event.target.value)
                            }
                            label="Type of Analysis"
                        >
                            <MenuItem value={'0'}>
                                The content of the files
                            </MenuItem>
                            <MenuItem value={'1'}>
                                Connections between the files (Obsidian style)
                            </MenuItem>
                        </Select>
                        <FormHelperText sx={{ marginTop: 2 }}>
                            You have an option to include the file names in the
                            graph to see how they connect to the discourse, but
                            that makes the visualization and analysis more
                            complex.
                        </FormHelperText>
                    </FormControl>
                    <FormControl
                        fullWidth
                        sx={{
                            marginTop: 4,
                            marginBottom: 2,
                        }}
                    >
                        <InputLabel id="modifyAnalyzedText-label">
                            Build a knowledge graph from
                        </InputLabel>
                        <Select
                            labelId="modifyAnalyzedText-label"
                            id="modifyAnalyzedText"
                            value={modifyAnalyzedText}
                            onChange={event =>
                                setModifyAnalyzedText(event.target.value)
                            }
                            label="Build a knowledge graph from"
                        >
                            <MenuItem value={'none'} key="1">
                                The words used in text
                            </MenuItem>
                            <MenuItem value={'detectEntities'} key="2">
                                The words and detected entities
                            </MenuItem>
                            <MenuItem value={'extractEntitiesOnly'} key="3">
                                Only the detected entities
                            </MenuItem>
                        </Select>
                        {modifyAnalyzedText != 'none' && (
                            <FormHelperText sx={{ marginTop: 1 }}>
                                By default, we extract the words used in your
                                text. You can also choose to detect and tag
                                entities in your text and build a graph from
                                them.
                            </FormHelperText>
                        )}
                    </FormControl>
                    {modifyAnalyzedText != 'none' && (
                        <FormControl
                            fullWidth
                            sx={{
                                marginTop: 2,
                                marginBottom: 2,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        id="replaceEntities"
                                        checked={replaceEntities}
                                        onChange={event =>
                                            setReplaceEntities(
                                                event.target.checked
                                            )
                                        }
                                    />
                                }
                                label="Replace entities with root concepts"
                            />
                            {replaceEntities && (
                                <FormHelperText
                                    id="replaceEntities-helperText"
                                    sx={{ marginTop: 1 }}
                                >
                                    When turned on, we will not only detect the
                                    entities, but also replace them with their
                                    ontological root concepts throughout your
                                    text.
                                </FormHelperText>
                            )}
                        </FormControl>
                    )}
                    {fileType != 'pdf' && (
                        <FormControl
                            fullWidth
                            sx={{
                                marginTop: 2,
                                marginBottom: 2,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={filterBySpeakers}
                                        onChange={event =>
                                            setFilterBySpeakers(
                                                event.target.checked
                                            )
                                        }
                                    />
                                }
                                label="Treat as an audio transcript"
                            />

                            <FormHelperText>
                                add category filters for each paragraph starting
                                with the speaker's name followed by :
                            </FormHelperText>
                        </FormControl>
                    )}
                </Grid>

                <Grid item xs={12} sm={5}>
                    <FormControl fullWidth sx={{ marginTop: 4 }}>
                        <EditableSelect
                            selectItems={contextNames}
                            label={'Import To'}
                            initialValue={selectedContext}
                            onChange={name => addToSelectedContext(name)}
                            aria-describedby="contextName-helperText"
                            id="contextName"
                        />
                        <FormHelperText
                            id="contextName-helperText"
                            sx={{ marginTop: 2 }}
                        >
                            Please, choose the name of the graph to import your
                            content to and click enter.
                        </FormHelperText>
                    </FormControl>

                    {fileType !== 'pdf' && (
                        <FormControl
                            fullWidth
                            sx={{
                                marginTop: 1,
                                marginBottom: 2,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={advancedSettings}
                                        onChange={event =>
                                            setAdvancedSettings(
                                                event.target.checked
                                            )
                                        }
                                    />
                                }
                                label="Show [[wiki link]] processing rules"
                            />
                        </FormControl>
                    )}

                    {advancedSettings && (
                        <>
                            {fileType !== 'pdf' && (
                                <FormControl fullWidth sx={{ marginTop: 4 }}>
                                    <InputLabel id="doubleSquareBrackets-label">
                                        Processing Rules for [[Wiki Links]] and
                                        Concepts
                                    </InputLabel>
                                    <Select
                                        labelId="doubleSquareBrackets-label"
                                        id="doubleSquareBrackets"
                                        value={doubleSquareBrackets.toString()}
                                        onChange={event =>
                                            setDoubleSquareBrackets(
                                                event.target.value
                                            )
                                        }
                                        label="Processing Rules for [[Wiki Links]] and Concepts"
                                        renderValue={selected =>
                                            (
                                                WIKILINKS_OPTIONS.find(
                                                    option =>
                                                        option.value ===
                                                        selected
                                                ) || {}
                                            ).name || selected
                                        }
                                    >
                                        {WIKILINKS_OPTIONS.map(
                                            ({ value, name, caption }) => (
                                                <MenuItem
                                                    key={value}
                                                    value={value}
                                                    sx={{
                                                        whiteSpace: 'normal',
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            display="block"
                                                        >
                                                            {caption}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                    <FormHelperText
                                        sx={{
                                            marginTop: 2,
                                        }}
                                    >
                                        Choose how the [[wiki-links]] in your
                                        notes become part of the graph.
                                    </FormHelperText>
                                </FormControl>
                            )}
                            {fileType != 'pdf' && (
                                <FormControl fullWidth sx={{ marginTop: 4 }}>
                                    <InputLabel id="squareBrackets-label">
                                        How to Treat [Square Brackets]
                                    </InputLabel>
                                    <Select
                                        labelId="squareBrackets-label"
                                        id="squareBrackets"
                                        value={squareBrackets.toString()}
                                        onChange={event =>
                                            setSquareBrackets(
                                                event.target.value
                                            )
                                        }
                                        label="How to Treat [Square Brackets]"
                                        renderValue={selected =>
                                            (
                                                SQUARE_BRACKETS_OPTIONS.find(
                                                    option =>
                                                        option.value ===
                                                        selected
                                                ) || {}
                                            ).name || selected
                                        }
                                    >
                                        {SQUARE_BRACKETS_OPTIONS.map(
                                            ({ value, name, caption }) => (
                                                <MenuItem
                                                    key={value}
                                                    value={value}
                                                    sx={{
                                                        whiteSpace: 'normal',
                                                    }}
                                                >
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            display="block"
                                                        >
                                                            {caption}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            )
                                        )}
                                    </Select>
                                </FormControl>
                            )}
                        </>
                    )}
                </Grid>
            </Grid>
        </AdvancedImportSettings>
    )

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle
                    sx={{
                        padding: 4,
                        color: theme => theme.palette.text.primary,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {topHeader[presetMode]}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={onClose}
                        aria-label="close"
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                {/* A stepper whose labels submit on click would be the most prominent control left. */}
                {!simplifyImport && (
                    <Stepper
                        activeStep={activeStep}
                        sx={{ paddingLeft: 4, paddingRight: 4, paddingTop: 2 }}
                    >
                        {steps.map((label, index) => {
                            const stepProps = {}
                            const labelProps = {}
                            // if (isStepOptional(index)) {
                            //     labelProps.optional = (
                            //         <Typography variant="caption">
                            //             Optional
                            //         </Typography>
                            //     )
                            // }
                            if (isStepSkipped(index)) {
                                stepProps.completed = false
                            }
                            return (
                                <Step key={label} {...stepProps}>
                                    <StepButton
                                        onClick={() => handleStep(index)}
                                        completed={(
                                            completedSteps.indexOf(index) >= 0
                                        ).toString()}
                                    >
                                        <StepLabel
                                            {...labelProps}
                                            sx={{
                                                '& .MuiStepLabel-label': {
                                                    fontSize: {
                                                        xs: '0.75rem',
                                                        sm: 'initial',
                                                    },
                                                },
                                            }}
                                        >
                                            {label}
                                        </StepLabel>
                                    </StepButton>
                                </Step>
                            )
                        })}
                    </Stepper>
                )}

                {errorMessage && (
                    <Typography
                        sx={{
                            paddingRight: 4,
                            paddingLeft: 4,
                            paddingTop: 4,
                            paddingBottom: 0,
                        }}
                        color="error"
                        component="div"
                        gutterBottom
                    >
                        {errorMessage}
                    </Typography>
                )}

                {activeStep === 1 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            <Box sx={{ p: 2 }}>
                                <Paper
                                    {...getRootProps()}
                                    onClick={openFileDialog} // Here we make the Paper component click event trigger the input click
                                    sx={{
                                        p: 2,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        textAlign: 'center',
                                        height: '200px',
                                        borderStyle: 'dashed',
                                        borderColor: isDragActive
                                            ? 'primary.main'
                                            : 'grey.400',
                                        backgroundColor: isDragActive
                                            ? 'background.default'
                                            : 'background.paper',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {presetMode == 'mdFilesImport' && (
                                        <Typography variant="body1">
                                            Drag and drop the markdown (md)
                                            files (you can export them from
                                            Obsidian, Roam, or LogSeq), or click
                                            to select and upload manually.
                                        </Typography>
                                    )}
                                    {presetMode == 'srtFilesImport' && (
                                        <Typography variant="body1">
                                            Drag and drop the audio transcript
                                            (.srt) files, or click to select and
                                            upload manually.
                                        </Typography>
                                    )}
                                    {presetMode == 'default' && (
                                        <Typography variant="body1">
                                            Upload or drag and drop the .txt,
                                            .pdf, .md (markdown)., .srt (audio
                                            transcript), .gexf (Gephi), .docx,
                                            .rtf, .csv (spreadsheet), or .json
                                            files you'd like to analyze here, or
                                            click to select and upload manually.
                                        </Typography>
                                    )}

                                    <input
                                        {...getInputProps()}
                                        ref={fileInputRef}
                                        accept="application/pdf, application/octet-stream, text/plain, text/markdown, text/srt, application/vnd.ms-excel, text/csv, .pdf, .txt, .srt, .md, .csv, .gexf, application/vnd.openxmlformats-officedocument.wordprocessingml.document, .docx, text/rtf, application/rtf, .rtf, .json, .jsonl, .ndjson, application/json, application/x-ndjson, application/jsonlines, application/ndjson"
                                        onChange={onFilesAdded}
                                        style={{ display: 'none' }}
                                    />
                                </Paper>
                                <Box sx={{ m: 2 }}>
                                    <Button
                                        variant="contained"
                                        component="label"
                                        onClick={openFileDialog}
                                    >
                                        Select & Upload Files
                                    </Button>
                                </Box>

                                <List>{fileList}</List>

                                {simplifyImport && fileProcessingSettings}
                            </Box>
                        </DialogContent>
                    </>
                )}
                {activeStep === 2 && (
                    <>
                        <DialogContent
                            sx={{
                                paddingRight: 4,
                                paddingLeft: 4,
                                paddingTop: 4,
                            }}
                        >
                            {!simplifyImport && (
                                <DialogContentText>
                                    Please, confirm how the files should be
                                    processed:
                                </DialogContentText>
                            )}

                            {fileProcessingSettings}
                        </DialogContent>
                    </>
                )}

                {activeStep === 3 && (
                    <ImportingNotice
                        linkHref={`/${username}/${selectedContext}/rss`}
                    />
                )}
                <DialogActions
                    sx={{
                        paddingRight: 4,
                        paddingLeft: 4,
                        paddingBottom: 4,
                        paddingTop: 2,
                    }}
                >
                    {/* <Button onClick={onClose} variant="outlined">
                        Cancel
                    </Button> */}
                    <Button
                        color="inherit"
                        variant="outlined"
                        disabled={activeStep === 0}
                        onClick={handleBack}
                        sx={{ mr: 1, px: 3, py: 1, fontSize: '0.95rem' }}
                    >
                        Back
                    </Button>
                    {/* {isStepOptional(activeStep) && (
                        <Button
                            color="inherit"
                            onClick={handleSkip}
                            sx={{ mr: 1 }}
                        >
                            Skip
                        </Button>
                    )} */}
                    <LoadingButton
                        color="info"
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        disabled={!files.length > 0}
                        onClick={handleNext}
                        sx={{ px: 4, py: 1, fontSize: '0.95rem' }}
                    >
                        {simplifyImport && activeStep === SOURCES_STEP
                            ? 'Start processing'
                            : activeStep === steps.length - 1
                            ? 'Visualize'
                            : 'Next'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    )
}

function renderImportOptionsMenu(receivedMenuItems) {
    const theme = useTheme()

    return (
        <Container
            sx={{
                py: 1,
                [theme.breakpoints.up('sm')]: {
                    pl: 0,
                },
                pt: 4,
            }}
            maxWidth={false}
        >
            <Grid
                container
                rowSpacing={3}
                justifyContent="flex-start"
                columnSpacing={3}
                alignItems="flex-start"
                pr={0}
            >
                {receivedMenuItems.map(card => (
                    <Grid item key={card.name} alignItems="flex-start" pr={0}>
                        <SubImportMenuItem {...card} />
                    </Grid>
                ))}
            </Grid>
        </Container>
    )
}

function generatePresetContext(type, fileName) {
    const dateAndTime = new Date()
        .toISOString()
        .slice(2, 16)
        .replace(/-/g, '')
        .replace(/:/g, '')

    const fileTypeSlug = type ? `${type}_` : ''
    const dateAndTimeSlug = !fileName ? `_${dateAndTime}` : ''
    const fileNameSlug = fileName
        ? `_${slugifyString(fileName).slice(0, 12)}`
        : ''
    return `${fileTypeSlug}${dateAndTimeSlug}${fileNameSlug}`
}

const determineFileType = file => {
    if (file.type === 'application/pdf') return '.pdf'
    else if (
        file.type ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    )
        return '.docx'
    else if (file.type === 'text/rtf' || file.type === 'application/rtf')
        return '.rtf'
    else if (file.type === 'text/plain') return '.txt'
    else if (file.type === 'text/markdown' || file.name.endsWith('.md'))
        return '.md'
    else if (
        file.type === 'application/octet-stream' &&
        file.name.endsWith('.srt')
    )
        return '.srt'
    else if (
        file.type === 'application/octet-stream' &&
        file.name.endsWith('.md')
    )
        return '.md'
    else if (
        file.type === 'application/octet-stream' &&
        file.name.endsWith('.pdf')
    )
        return '.pdf'
    else if (
        (!file.type || file.type === 'application/octet-stream') &&
        file.name.endsWith('.gexf')
    )
        return '.gexf'
    else if (file.type === 'text/markdown' || file.name.endsWith('.md'))
        return '.md'
    else if (file.name.endsWith('.srt')) return '.srt'
    else if (file.type === 'application/vnd.ms-excel') return '.csv'
    else if (file.name.endsWith('.csv')) return '.csv'
    else if (
        file.type === 'application/json' ||
        file.type === 'application/x-ndjson' ||
        file.type === 'application/jsonlines' ||
        file.type === 'application/ndjson'
    )
        return '.json'
    else if (file.name.endsWith('.json')) return '.json'
    else if (file.name.endsWith('.jsonl')) return '.jsonl'
    else if (file.name.endsWith('.ndjson')) return '.ndjson'
    else return null
}
